"use client";
import React, {
  useEffect,
  useCallback,
  useState,
  useRef,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { MenuOutlined, BellOutlined } from "@ant-design/icons";
import { Badge, Button, Dropdown, Image, Spin, Empty, Divider, message } from "antd";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import FeedbackPopup from "./FeedbackPopup/FeedbackPopup.jsx";
import PrivacyPolicyPopup from "./PrivacyPolicyPopup/PrivacyPolicyPopup.jsx";
import DoctorStatusBanner from "./DoctorStatusBanner/DoctorStatusBanner.jsx";
import RejectedDoctorModal from "./RejectedDoctorModal/RejectedDoctorModal.jsx";
import SwitchToPatientModal from "./switchModalOpen/switchModalOpen.jsx";
import {fetchDoctors,getCurrentDoctor} from "../../authDoctor/redux/thunks/doctorsThunks.js";
import { logoutDoctorLocal } from "../../authDoctor/redux/slices/doctorsSlice.js";
import { markSessionLogout } from "../../auth/sessionLogout.js";
import { fetchDoctorInfoByDoctorId } from "../../ProfileDoctor/redux/thunks/doctorProfileThunks.js";
import useDoctorInformation from "../../ProfileDoctor/hooks/useDoctorInformation.js";
import useDoctorProfileCompletion from "./useDoctorProfileCompletion.js";
import { selectRdvByMedecin } from "../../appointments/redux/selectors/rendezvousSelectors.js";
import { useMessageContactsForDoctor } from "../../messagerie/hooks/useMessageContactsForDoctor.js";
import { useTheme } from "../../places/context/ThemeContext.jsx";
import LanguageSelector from "../../Landing/components/LanguageSelector/LanguageSelector.jsx";
import NavbarRadio from "./NavbarRadio.jsx";
import NavbarDateWeather from "./NavbarDateWeather.jsx";
import useNotifications from "../../notifications/hooks/useNotifications.js";
import { enablePushNotifications } from "../../notifications/pushNotifications.js";
import { getNotificationRoute } from "../../notifications/getNotificationRoute.js";
import { fetchUsers } from "../../Profile/redux/thunks/userThunks.js";
import api, { axiosDoctorRegister } from "../../api/axios.js";
import { DOCTOR_ROUTES } from "../../routes/patientRoutes/routePaths.js";
import "./Navbar.css";
import navbarSearchMutedIcon from "../../assets/icons/svg/navbar-search-muted.svg";

const normalizeDoctorStatus = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const s = String(value).trim().toLowerCase();
  if (s === "pending") return "pending";
  if (s === "approved") return "approved";
  if (s === "rejected") return "rejected";
  return null;
};
const isUuid = (s) =>
  typeof s === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);
const normId = (v) => {
  if (v == null) return null;
  let s = String(v).trim();
  if (!s) return null;
  if (s.startsWith("DR_") || s.startsWith("dr_")) s = s.slice(3);
  if (s.startsWith("PAT_") || s.startsWith("pat_")) s = s.slice(4);
  return s.trim() || null;
};
const isNumericLike = (v) => {
  const s = normId(v);
  if (!s) return false;
  const n = Number(s);
  return Number.isFinite(n) && String(n) === String(Number(s));
};
const collectUserIds = (u) => {
  if (!u) return [];
  const raw = u?.raw || {};
  const user = u?.user || raw?.user || {};
  const ids = [
    u?.id, u?._id, u?.userId, u?.pgId, u?.uuid, u?.patientId, u?.patient_id,
    raw?.id, raw?._id, raw?.userId, raw?.pgId, raw?.uuid, raw?.patientId,
    user?.id, user?._id, user?.userId, user?.pgId, user?.uuid,
  ].map(normId).filter(Boolean);
  const numeric = ids.map((x) => {
    const n = Number(x);
    return Number.isFinite(n) ? String(n) : null;
  }).filter(Boolean);
  return Array.from(new Set([...ids, ...numeric]));
};
const extractFullName = (u) => {
  if (!u) return null;
  const raw = u?.raw || {};
  const user = u?.user || raw?.user || {};
  const profile = u?.profile || raw?.profile || {};
  const candidates = [
    u?.fullName, raw?.fullName, user?.fullName, profile?.fullName,
    u?.displayName, raw?.displayName, user?.displayName,
    u?.name, raw?.name, user?.name,
  ];
  for (const c of candidates) {
    if (c && typeof c === "string" && c.trim().length > 1) return c.trim();
  }
  const firstNameVariants = [
    u?.firstName, raw?.firstName, user?.firstName, profile?.firstName,
    u?.prenom, raw?.prenom, user?.prenom, profile?.prenom,
  ];
  const lastNameVariants = [
    u?.lastName, raw?.lastName, user?.lastName, profile?.lastName,
    u?.nom, raw?.nom, user?.nom, profile?.nom,
  ];
  let firstName = null;
  let lastName = null;
  for (const fn of firstNameVariants) {
    if (fn && typeof fn === "string" && fn.trim().length > 0) { firstName = fn.trim(); break; }
  }
  for (const ln of lastNameVariants) {
    if (ln && typeof ln === "string" && ln.trim().length > 0) { lastName = ln.trim(); break; }
  }
  if (firstName && lastName) return `${firstName} ${lastName}`;
  if (firstName) return firstName;
  if (lastName) return lastName;
  const email = u?.email || raw?.email || user?.email;
  if (email && typeof email === "string" && email.includes("@")) return email.split("@")[0];
  return null;
};
const formatDoctorName = (name) => {
  const clean = String(name || "").trim();
  if (!clean || clean.toLowerCase() === "unknown user") return "Dr. Doctor";
  return /^dr\.?\s*/i.test(clean)
    ? clean.replace(/^dr\.?\s*/i, "Dr. ")
    : `Dr. ${clean}`;
};
const getInitialsFromUser = (user, fallback = "DR") => {
  const firstName = String(user?.firstName || "").trim();
  const lastName = String(user?.lastName || "").trim();
  if (firstName || lastName) {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || fallback;
  }
  const fullName = user?.fullName || user?.name || user?.displayName || user?.email?.split("@")?.[0] || "";
  const parts = String(fullName).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return fallback;
};

const isValidAvatarSrc = (src) => {
  if (!src || typeof src !== "string") return false;
  const clean = src.trim();
  if (!clean) return false;
  const lower = clean.toLowerCase();
  const invalidPatterns = [
    "cdn-icons-png.flaticon.com/512/149/149071.png", "149071.png",
    "default-avatar", "default_avatar", "placeholder", "avatar-placeholder",
    "undefined", "null",
  ];
  return !invalidPatterns.some((pattern) => lower.includes(pattern));
};

const formatDateTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const dateStr = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const timeStr = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${dateStr} at ${timeStr}`;
};

const extractSenderIdFromNotif = (n) => {
  const meta = n?.metadata || n?.data || n?._raw?.metadata || {};
  const candidates = [
    meta?.fromUserId, n?.fromUserId, meta?.senderId, n?.senderId,
    meta?.from, meta?.sender, meta?.patientId, n?.patientId,
    meta?.userId, n?.userId, meta?.requesterId, n?.requesterId,
    meta?.grantedBy, n?.grantedBy, meta?.grantedTo, n?.grantedTo,
    meta?.targetUserId, n?.targetUserId,
  ];
  for (const c of candidates) {
    const v = normId(c);
    if (v) return v;
  }
  return null;
};

const getNotifMeta = (n) => n?.metadata || n?.data || n?._raw?.metadata || {};

const getEventLower = (n) => {
  const meta = getNotifMeta(n);
  return `${String(n?.event || "").toLowerCase()} ${String(n?.type || "").toLowerCase()} ${String(meta?.event || "").toLowerCase()}`.trim();
};

const isAppointmentNotif = (n) => {
  const ev = getEventLower(n);
  const type = String(n?.type || "").toLowerCase();
  return (
    type.includes("appointment") || type.includes("rendezvous") || type.includes("rdv") ||
    ev.includes("appointment") || ev.includes("rendezvous") || ev.includes("rdv")
  );
};

const isCancelledAppointmentNotif = (n) => {
  const meta = getNotifMeta(n);
  const ev = getEventLower(n);
  const status = String(meta?.status || n?.status || "").toLowerCase();
  const cancelledBy = String(meta?.cancelledBy || "").toUpperCase();
  return ev.includes("cancel") || status.includes("cancel") || cancelledBy === "PATIENT" || cancelledBy === "DOCTOR";
};

const isConfirmedAppointmentNotif = (n) => {
  const ev = getEventLower(n);
  return (ev.includes("confirm") || ev.includes("confirmed")) && !isCancelledAppointmentNotif(n);
};

const isAccessGrantedNotification = (n) => {
  const meta = getNotifMeta(n);
  const text = [
    n?.title,
    n?.message,
    n?.body,
    n?.event,
    n?.type,
    meta?.title,
    meta?.event,
    meta?.type,
    meta?.status,
    meta?.patientName,
    n?.patientName,
  ].filter(Boolean).join(" ").toLowerCase();

  return (
    text.includes("access granted") ||
    text.includes("data access") ||
    text.includes("medical records access") ||
    text.includes("access request approved") ||
    (text.includes("access") && text.includes("approved")) ||
    (text.includes("access") && text.includes("granted"))
  );
};

const isCommentNotif = (n) => {
  const type = String(n?.type || "").toLowerCase();
  const ev = getEventLower(n);
  return type === "comment" || ev.includes("comment.created") || ev.includes("comment");
};

const safeT = (t, key, vars, fallback) => {
  const result = t(key, vars);
  return result === key ? fallback : result;
};

const resolveAsyncName = ({
  notifId, fromId, asyncNames, setAsyncNames,
  resolvePatientKeyForMessaging, fetchPatientNameFromApiNumericOnly,
}) => {
  if (!fromId || asyncNames[notifId]) return;
  const numericKey = resolvePatientKeyForMessaging(fromId);
  const keyToFetch = numericKey && isNumericLike(numericKey) ? numericKey : isUuid(fromId) ? fromId : null;
  if (!keyToFetch) return;
  setAsyncNames((prev) => ({ ...prev, [notifId]: "__loading__" }));
  fetchPatientNameFromApiNumericOnly(keyToFetch)
    .then((name) => setAsyncNames((prev) => ({ ...prev, [notifId]: name || "__failed__" })))
    .catch(() => setAsyncNames((prev) => ({ ...prev, [notifId]: "__failed__" })));
};

/* =======================
   Main Component
======================= */

export default function NavbarDoctor({
  collapsed,
  toggleCollapsed,
  isMobile,
  onMobileMenuToggle,
  onMessage,
}) {
  const { t, i18n } = useTranslation();
  const isRTL = (i18n.language || "").startsWith("ar");
  const searchTranslationKey = (key) =>
    `${isRTL ? "header" : "submenu"}.search.${key}`;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { toggleTheme, setTheme, theme } = useTheme();

  const [notifOpen, setNotifOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [profileRefreshKey, setProfileRefreshKey] = useState(0);
  const [asyncNames, setAsyncNames] = useState({});
  const nameCacheRef = useRef(new Map());
  const pendingFetchesRef = useRef(new Set());

  const [searchValue, setSearchValue] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [avatarLoadError, setAvatarLoadError] = useState(false);

  // ✅ Modal rejected : s'ouvre seulement au login OU au clic sur le badge
  const [rejectedModalOpen, setRejectedModalOpen] = useState(false);

  // ✅ Modal switch vers le mode Patient
  const [switchPatientOpen, setSwitchPatientOpen] = useState(false);

  // ✅ Statut live récupéré directement depuis /me
  const [liveDoctorStatus, setLiveDoctorStatus] = useState(null);
  const [liveDoctorRejectionReason, setLiveDoctorRejectionReason] = useState(null);

  const { user: currentUser } = useSelector((state) => state.doctor || {});
  const currentDoctorId = normId(
    currentUser?.doctorId || currentUser?.pgId || currentUser?.id
  );

  // ✅ Statut final : priorité au live, fallback Redux
  const doctorStatus = useMemo(() => {
    if (liveDoctorStatus) return liveDoctorStatus;
    const fromRedux = normalizeDoctorStatus(
      currentUser?.status || currentUser?.raw?.status
    );
    return fromRedux;
  }, [liveDoctorStatus, currentUser]);

  const doctorRejectionReason = useMemo(() => {
    return (
      liveDoctorRejectionReason ||
      currentUser?.rejectionReason ||
      currentUser?.raw?.rejectionReason ||
      null
    );
  }, [liveDoctorRejectionReason, currentUser]);

  const patientsList = useSelector((state) => {
    const s = state.user || {};
    const byId = s.usersById;

    if (byId && typeof byId === "object" && Object.keys(byId).length > 0) {
      return Object.values(byId);
    }

    const fallback = s.data || s.users || s.items || s.list || [];
    if (Array.isArray(fallback)) return fallback;
    if (fallback && typeof fallback === "object") return Object.values(fallback);

    return [];
  }, shallowEqual);

  const doctorsLoaded = useSelector((state) => state.doctor.doctorsLoaded);
  const doctorsList = useSelector((state) => (
    Array.isArray(state.doctor?.doctors) ? state.doctor.doctors : []
  ));
  const rdvByMedecin = useSelector(selectRdvByMedecin) || [];
  const { contacts: messageContacts = [] } = useMessageContactsForDoctor();
  const { avatarSrc } = useDoctorInformation(currentUser);

  const {
    percent: profileCompletion,
    missing: missingFields,
    loading: completionLoading,
  } = useDoctorProfileCompletion();

  const isProfileComplete = !completionLoading && (missingFields?.length ?? 0) === 0;
  const safeDoctorCompletionPercent = Math.max(0, Math.min(100, Number(profileCompletion) || 0));
  const hasDoctorAvatar = isValidAvatarSrc(avatarSrc) && !avatarLoadError;
  const doctorInitials = getInitialsFromUser(currentUser, "DR");

  const { isLoading: notifLoading, markRead, markAllRead } = useNotifications({
    pageSize: 20, latestCount: 10, enableWs: false, autoFetch: true, role: "doctor",
  });

  const notifState = useSelector((state) => state.notifications || {});
  const { items: rtNotifications = [], unreadCount: rtUnreadCount = 0 } = notifState;

  useEffect(() => { setAvatarLoadError(false); }, [avatarSrc]);

  useEffect(() => {
    const onUpd = () => setProfileRefreshKey((k) => k + 1);
    window.addEventListener("doctorProfileUpdated", onUpd);
    return () => window.removeEventListener("doctorProfileUpdated", onUpd);
  }, []);

  // ✅ Fetch direct /me au montage pour avoir le statut à jour
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const token =
          Cookies.get("doctorAccessToken") ||
          Cookies.get("doctorToken") ||
          localStorage.getItem("doctorAccessToken") ||
          localStorage.getItem("doctorToken") ||
          null;

        const res = await axiosDoctorRegister.get("/me", {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        const data = res.data?.data || res.data || {};
        const status = normalizeDoctorStatus(data.status || data.raw?.status);
        const reason = data.rejectionReason || data.raw?.rejectionReason || null;

        setLiveDoctorStatus(status);
        setLiveDoctorRejectionReason(reason);

        // ✅ Ouvre la modal rejected UNIQUEMENT si le flag login est présent
        if (status === "rejected") {
          const shouldShow = sessionStorage.getItem("showRejectedModal");
          if (shouldShow === "true") {
            sessionStorage.removeItem("showRejectedModal");
            setRejectedModalOpen(true);
          }
        }
      } catch (err) {
        console.error("[NavbarDoctor] Failed to fetch doctor status:", err);
      }
    };

    dispatch(getCurrentDoctor());
    fetchStatus();
  }, [dispatch]);

  const doctorIdForProfile = useMemo(
    () =>
      normId(currentUser?.id) ||
      normId(currentUser?.doctorId) ||
      normId(currentUser?.userId) ||
      normId(currentUser?._id) ||
      null,
    [currentUser]
  );

  useEffect(() => {
    if (doctorsLoaded) return;
    dispatch(fetchDoctors());
  }, [dispatch, doctorsLoaded]);

  useEffect(() => {
    if (!doctorIdForProfile) return;
    dispatch(fetchDoctorInfoByDoctorId(doctorIdForProfile));
  }, [dispatch, doctorIdForProfile, profileRefreshKey]);

  const fetchedUsersRef = useRef(false);
  useEffect(() => {
    if (fetchedUsersRef.current) return;
    if (Array.isArray(patientsList) && patientsList.length > 0) return;
    fetchedUsersRef.current = true;
    dispatch(fetchUsers());
  }, [dispatch, patientsList]);

  const logout = useCallback(() => {
    markSessionLogout("doctor");
    dispatch(logoutDoctorLocal());
    setTheme("light");
    navigate("/LoginDoctor");
  }, [dispatch, navigate, setTheme]);

  const findPatientByAnyId = useCallback(
    (anyId) => {
      const key = normId(anyId);
      if (!key) return null;
      const list = Array.isArray(patientsList) ? patientsList : [];
      return list.find((p) => collectUserIds(p).includes(key)) || null;
    },
    [patientsList]
  );

  const resolvePatientKeyForMessaging = useCallback(
    (senderId) => {
      const raw = normId(senderId);
      if (!raw) return null;
      if (isUuid(raw)) {
        const p = findPatientByAnyId(raw);
        return normId(p?.id) || normId(p?.pgId) || normId(p?.userId) || raw;
      }
      return raw;
    },
    [findPatientByAnyId]
  );

  const fetchPatientNameFromApiNumericOnly = useCallback(async (patientId) => {
    const pid = normId(patientId);
    if (!pid) return null;
    // A doctor can't be their own patient — this shows up when a stray
    // self-addressed message/notification has fromId === this doctor's id,
    // which would otherwise 404 against /users/:id on every retry.
    if (currentDoctorId && pid === currentDoctorId) return null;
    const cacheKey = `patient:${pid}`;
    if (nameCacheRef.current.has(cacheKey)) return nameCacheRef.current.get(cacheKey);
    if (pendingFetchesRef.current.has(cacheKey)) return null;
    pendingFetchesRef.current.add(cacheKey);
    try {
      const res = await api.get(`/users/${encodeURIComponent(pid)}`, { withCredentials: true, timeout: 5000 });
      const patient = res.data?.data ?? res.data;
      const name = extractFullName(patient);
      // Cache the miss too (not just successes) so a name that keeps
      // failing to resolve doesn't get re-fetched on every re-render.
      nameCacheRef.current.set(cacheKey, name || null);
      return name || null;
    } catch {
      nameCacheRef.current.set(cacheKey, null);
      return null;
    } finally {
      pendingFetchesRef.current.delete(cacheKey);
    }
  }, [currentDoctorId]);

  const resolvePatientNameById = useCallback(
    (fromUserId) => {
      const fidRaw = normId(fromUserId);
      if (!fidRaw) return null;
      const cacheKey = `patient:${fidRaw}`;
      if (nameCacheRef.current.has(cacheKey)) return nameCacheRef.current.get(cacheKey);
      const patient = findPatientByAnyId(fidRaw);
      if (patient) {
        const name = extractFullName(patient);
        if (name) { nameCacheRef.current.set(cacheKey, name); return name; }
      }
      return null;
    },
    [findPatientByAnyId]
  );

  const hasAppointment = useCallback(
    (patient) => {
      if (!rdvByMedecin?.length) return false;
      const patientId = normId(patient?.id) || normId(patient?.patientId) || normId(patient?.userId) || normId(patient?.pgId);
      if (!patientId) return false;
      return rdvByMedecin.some((rdv) => {
        const rdvPatientId = normId(rdv?.patientId) || normId(rdv?.patient?.id) || normId(rdv?.patient?.patientId);
        return normId(rdvPatientId) === patientId;
      });
    },
    [rdvByMedecin]
  );

  const isInMessageContacts = useCallback(
    (patient) => {
      if (!messageContacts?.length) return false;
      const patientId = normId(patient?.id) || normId(patient?.patientId) || normId(patient?.userId) || normId(patient?.pgId);
      if (!patientId) return false;
      return messageContacts.some((contact) => {
        const contactPatientId = normId(contact?.patientId) || normId(contact?.userId) || normId(contact?.peerId) || normId(contact?.id);
        return normId(contactPatientId) === patientId;
      });
    },
    [messageContacts]
  );

  const searchSuggestions = useMemo(() => {
    if (!searchValue) return [];
    const q = searchValue.toLowerCase().trim();
    if (!q) return [];
    const matchesQuery = (p) => {
        const firstName = (p.firstName || "").toLowerCase().trim();
        const lastName = (p.lastName || "").toLowerCase().trim();
        const fullName = `${firstName} ${lastName}`.toLowerCase().trim();
        const email = (p.email || "").toLowerCase().trim();
        const phone = (p.phone || "").toLowerCase().trim();
        const nestedFirstName = (p?.user?.firstName || p?.raw?.firstName || "").toLowerCase().trim();
        const nestedLastName = (p?.user?.lastName || p?.raw?.lastName || "").toLowerCase().trim();
        const nestedFullName = `${nestedFirstName} ${nestedLastName}`.toLowerCase().trim();
        const nestedEmail = (p?.user?.email || p?.raw?.email || "").toLowerCase().trim();
        return (
          fullName.includes(q) || firstName.includes(q) || lastName.includes(q) ||
          email.includes(q) || phone.includes(q) || nestedFullName.includes(q) ||
          nestedFirstName.includes(q) || nestedLastName.includes(q) || nestedEmail.includes(q)
        );
    };

    const doctors = (Array.isArray(doctorsList) ? doctorsList : [])
      .filter((doctor) => normId(doctor?.id || doctor?.doctorId || doctor?.userId) !== currentDoctorId)
      .filter(matchesQuery)
      .map((doctor) => ({ ...doctor, _searchRole: "doctor" }));
    const patients = (Array.isArray(patientsList) ? patientsList : [])
      .filter(matchesQuery)
      .map((patient) => ({ ...patient, _searchRole: "patient" }));

    return [...doctors, ...patients].slice(0, 10);
  }, [currentDoctorId, doctorsList, patientsList, searchValue]);

  // ✅ Clic sur un résultat de recherche -> navigation directe vers le profil
  //    - Doctor  -> page de profil docteur (DOCTOR_ROUTES.PROFILE)
  //    - Patient -> page overview du patient (DoctorOverviewsPage)
  const handleSearchSelect = useCallback(
    (person) => {
      const personId =
        normId(person?.id) ||
        normId(person?.patientId) ||
        normId(person?.userId) ||
        normId(person?.pgId);
      if (!personId) return;

      if (person?._searchRole === "doctor") {
        // ⚠️ DOCTOR_ROUTES.PROFILE ("/doctorprofile") est la route du
        // docteur CONNECTÉ, sans paramètre : lui ajouter un id à la volée
        // produit une URL qui ne matche aucune route et fait sauter la
        // session (redirection vers login).
        // DoctorProfile.jsx lit l'id via useParams().doctorId, donc on
        // utilise la route dédiée "/doctor/:doctorId/profile".
        navigate(DOCTOR_ROUTES.PROFILE_DOCTOR(personId), {
          state: { doctorId: personId, doctor: person },
        });
      } else {
        navigate(`/doctor/patients/${personId}/DoctorOverviewsPage`, {
          state: { patientId: personId, patient: person, fromSearch: true },
        });
      }

      setSearchValue("");
      setSearchOpen(false);
    },
    [navigate]
  );

  const handleSearchKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter") return;
      if (!searchValue?.trim()) return;
      if (!searchSuggestions.length) return;

      event.preventDefault();
      handleSearchSelect(searchSuggestions[0]);
    },
    [handleSearchSelect, searchSuggestions, searchValue]
  );

  const formatNotifText = useCallback(
    (n) => {
      const meta = getNotifMeta(n);
      const notifId = String(n?.id || n?._id || Math.random());
      const rawType = String(n?.type || n?.event || "").toLowerCase();
      const isChat = rawType.includes("chat") || rawType.includes("message");
      const isRelative = rawType.includes("relative");
      const isPrescription = rawType.includes("prescription");
      const isAppt = isAppointmentNotif(n);
      const isAccess =
        rawType.includes("access") || rawType.includes("granted") || rawType.includes("permission") ||
        getEventLower(n).includes("access") || getEventLower(n).includes("granted");

      if (isCommentNotif(n)) {
        let commenterName = meta?.fromUserName || n?.fromUserName || meta?.senderName || n?.senderName || null;
        if (!commenterName || commenterName === "Unknown user") {
          const fromId = extractSenderIdFromNotif(n);
          if (fromId) commenterName = resolvePatientNameById(fromId);
        }
        if (!commenterName && !asyncNames[notifId]) {
          const fromId = extractSenderIdFromNotif(n);
          resolveAsyncName({ notifId, fromId, asyncNames, setAsyncNames, resolvePatientKeyForMessaging, fetchPatientNameFromApiNumericOnly });
        }
        if (asyncNames[notifId] === "__loading__") return safeT(t, "submenu.notifications.loadingMessage", {}, "Loading…");
        if (!commenterName && asyncNames[notifId] && asyncNames[notifId] !== "__failed__") commenterName = asyncNames[notifId];
        commenterName = commenterName || safeT(t, "submenu.notifications.someone", {}, "Someone");
        return safeT(t, "submenu.notifications.commentOnPost", { name: commenterName }, `${commenterName} commented on your post`);
      }

      if (isAccess) {
        let fromName = meta?.fromUserName || n?.fromUserName || meta?.senderName || n?.senderName ||
          meta?.patientName || n?.patientName || meta?.requesterName || n?.requesterName ||
          meta?.grantedByName || n?.grantedByName || null;
        if (!fromName || fromName === "Unknown user") {
          const fromId = extractSenderIdFromNotif(n);
          if (fromId) fromName = resolvePatientNameById(fromId);
        }
        if (!fromName && !asyncNames[notifId]) {
          const fromId = extractSenderIdFromNotif(n);
          resolveAsyncName({ notifId, fromId, asyncNames, setAsyncNames, resolvePatientKeyForMessaging, fetchPatientNameFromApiNumericOnly });
        }
        if (asyncNames[notifId] === "__loading__") return safeT(t, "submenu.notifications.loadingMessage", {}, "Loading…");
        if (!fromName && asyncNames[notifId] && asyncNames[notifId] !== "__failed__") fromName = asyncNames[notifId];
        fromName = fromName && fromName !== "__failed__" ? fromName : safeT(t, "submenu.notifications.aPatient", {}, "a patient");
        return safeT(t, "submenu.notifications.accessGrantedFrom", { name: fromName }, `Access granted from ${fromName}`);
      }

      if (isChat) {
        const isDoctorConversation =
          String(meta?.conversationType || "").toUpperCase() ===
            "DOCTOR_DOCTOR" ||
          String(meta?.fromRole || meta?.senderRole || "").toLowerCase() ===
            "doctor";
        let senderName = n?.fromUserName || n?.senderName || n?.senderFullName || n?.patientName || n?.userName || n?.name ||
          meta?.fromUserName || meta?.senderName || meta?.senderFullName || meta?.patientName ||
          meta?.userName || meta?.name || meta?.from?.name || meta?.from?.fullName ||
          meta?.sender?.name || meta?.sender?.fullName || null;
        if (!senderName || senderName === "Unknown user" || !senderName.trim()) senderName = null;
        const fromId = extractSenderIdFromNotif(n);
        if (isDoctorConversation && fromId) {
          const senderDoctor = doctorsList.find((doctor) =>
            collectUserIds(doctor).includes(fromId),
          );
          senderName =
            extractFullName(senderDoctor) ||
            senderName;
        }
        if (!senderName && asyncNames[notifId]) {
          const c = asyncNames[notifId];
          if (c !== "__loading__" && c !== "__failed__") senderName = c;
        }
        if (!isDoctorConversation && !senderName && fromId) {
          senderName = resolvePatientNameById(fromId);
        }
        if (!isDoctorConversation && !senderName && fromId && !asyncNames[notifId]) {
          resolveAsyncName({ notifId, fromId, asyncNames, setAsyncNames, resolvePatientKeyForMessaging, fetchPatientNameFromApiNumericOnly });
        }
        if (asyncNames[notifId] === "__loading__") return safeT(t, "submenu.notifications.loadingMessage", {}, "Loading…");
        if (!senderName && asyncNames[notifId] && asyncNames[notifId] !== "__failed__") senderName = asyncNames[notifId];
        if (isDoctorConversation) {
          return `${formatDoctorName(senderName || "Doctor")} sent you a message`;
        }
        if (senderName && senderName !== "__failed__") return safeT(t, "submenu.notifications.messageFrom", { name: senderName }, `Your received a new message from ${senderName}`);
        return safeT(t, "submenu.notifications.newMessage", {}, "New message");
      }

      if (isAppt) {
        let patientName = meta?.patientName || n?.patientName || null;
        if (!patientName || patientName === "Unknown user") {
          patientName = resolvePatientNameById(meta?.patientId || n?.patientId || extractSenderIdFromNotif(n));
        }
        patientName = patientName || safeT(t, "submenu.notifications.aPatient", {}, "a patient");
        const when = formatDateTime(meta?.startTime || n?.startTime || n?.date);
        const cancelled = isCancelledAppointmentNotif(n);
        const confirmed = isConfirmedAppointmentNotif(n);
        const requested = /request/.test(getEventLower(n));
        if (requested) {
          return safeT(t, "submenu.notifications.appointmentRequestedBy", { name: patientName }, `New appointment request from ${patientName}`);
        }
        if (cancelled) return when
          ? safeT(t, "submenu.notifications.appointmentCancelledWithWhen", { name: patientName, when }, `Appointment with ${patientName} on ${when} was cancelled`)
          : safeT(t, "submenu.notifications.appointmentCancelledWith", { name: patientName }, `Appointment with ${patientName} was cancelled`);
        if (confirmed) return when
          ? safeT(t, "submenu.notifications.appointmentConfirmedWithWhen", { name: patientName, when }, `Appointment with ${patientName} on ${when} is confirmed`)
          : safeT(t, "submenu.notifications.appointmentConfirmedWith", { name: patientName }, `Appointment with ${patientName} is confirmed`);
        return when
          ? safeT(t, "submenu.notifications.appointmentUpdatedWithWhen", { name: patientName, when }, `Appointment update with ${patientName} on ${when}`)
          : safeT(t, "submenu.notifications.appointmentUpdatedWith", { name: patientName }, `Appointment update with ${patientName}`);
      }

      if (isRelative) {
        let fromName = meta?.fromUserName || n?.fromUserName || null;
        if (!fromName || fromName === "Unknown user") fromName = resolvePatientNameById(extractSenderIdFromNotif(n));
        fromName = fromName || safeT(t, "submenu.notifications.aPatient", {}, "a patient");
        const relType = meta?.relationshipType ? ` (${meta.relationshipType})` : "";
        return safeT(t, "submenu.notifications.relativeRequest", { name: fromName, relType }, `${fromName} sent a relative request${relType}`);
      }

      if (isPrescription) {
        let patientName = meta?.patientName || n?.patientName || null;
        if (!patientName || patientName === "Unknown user") {
          patientName = resolvePatientNameById(meta?.patientId || n?.patientId || meta?.toUserId || n?.toUserId);
        }
        patientName = patientName || safeT(t, "submenu.notifications.aPatient", {}, "a patient");
        return safeT(t, "submenu.notifications.prescriptionAddedFor", { name: patientName }, `Prescription added for ${patientName}`);
      }

      return n?.title || n?.message || safeT(t, "submenu.notifications.default", {}, "New notification");
    },
    [resolvePatientNameById, asyncNames, doctorsList, fetchPatientNameFromApiNumericOnly, resolvePatientKeyForMessaging, t]
  );

  const handleNotifClick = useCallback(
    async (n) => {
      const id = n?.id ?? n?._id;
      if (id) { try { await markRead(id); } catch {} }
      const rawType = String(n?.type || n?.event || "").toLowerCase();
      const meta = getNotifMeta(n);
      const isChat = rawType.includes("chat") || rawType.includes("message") || String(meta?.event || "").toLowerCase().includes("message");
      const isAccess = isAccessGrantedNotification(n) || rawType.includes("access") || rawType.includes("granted") || rawType.includes("permission");

      if (isChat) {
        const senderRaw = extractSenderIdFromNotif(n);
        const isDoctorConversation =
          String(meta?.conversationType || "").toUpperCase() ===
            "DOCTOR_DOCTOR" ||
          String(meta?.fromRole || meta?.senderRole || "").toLowerCase() ===
            "doctor";
        if (isDoctorConversation) {
          const doctorId = normId(senderRaw);
          if (!doctorId) return;

          const senderDoctor = doctorsList.find((doctor) =>
            collectUserIds(doctor).includes(doctorId),
          );
          const realName =
            extractFullName(senderDoctor) ||
            meta?.fromUserName ||
            meta?.senderName ||
            `Dr. ${doctorId.slice(-6)}`;
          const conversationId = String(meta?.conversationId || "").trim();
          const selectedContact = {
            id: doctorId,
            userId: doctorId,
            peerId: doctorId,
            fullName: realName,
            name: realName,
            email: senderDoctor?.email || "",
            specialty: senderDoctor?.specialty,
            avatar:
              senderDoctor?.avatar ||
              senderDoctor?.avatarUrl ||
              senderDoctor?.photo ||
              "https://cdn-icons-png.flaticon.com/512/3774/3774299.png",
            meta: {
              type: "doctor",
              role: "doctor",
              doctorId,
              conversationId: conversationId || undefined,
              displayName: realName,
            },
          };

          sessionStorage.setItem(
            "pendingSelectedContact",
            JSON.stringify(selectedContact),
          );
          sessionStorage.setItem("autoSelectContactId", doctorId);
          const targetRoute = getNotificationRoute(n, "doctor");
          if (!targetRoute) return;
          navigate(targetRoute, {
            state: {
              selectedContact,
              autoOpen: true,
              fromNotif: true,
              fromUserId: doctorId,
              messageId: meta?.messageId || n?.messageId || null,
            },
          });
          setNotifOpen(false);
          return;
        }

        const patientKey = resolvePatientKeyForMessaging(senderRaw);
        const patientObj = findPatientByAnyId(senderRaw) || findPatientByAnyId(patientKey);
        const finalId = patientKey || normId(senderRaw);
        if (!finalId) return;
        let realName = extractFullName(patientObj) || n?.fromUserName || n?.senderName || n?.patientName ||
          meta?.fromUserName || meta?.senderName || meta?.senderFullName || meta?.patientName ||
          meta?.userName || meta?.from?.name || meta?.from?.fullName || meta?.sender?.name || meta?.sender?.fullName || null;
        if (!realName || String(realName).trim().toLowerCase() === "unknown user" || String(realName).trim().toLowerCase() === "unknown") {
          realName = resolvePatientNameById(senderRaw) || resolvePatientNameById(patientKey) || safeT(t, "submenu.defaults.patientName", {}, "Patient");
        }
        const avatar = patientObj?.avatar || patientObj?.avatarUrl || patientObj?.photo || "https://cdn-icons-png.flaticon.com/512/149/149071.png";
        const selectedContact = {
          id: finalId, _id: finalId, userId: finalId, peerId: finalId, patientId: finalId,
          fullName: realName, name: realName, displayName: realName, patientName: realName,
          avatar, photo: avatar, role: "patient", type: "patient", email: patientObj?.email || "",
          meta: { type: "patient", role: "patient", patientId: finalId, registerId: finalId, displayName: realName, fullName: realName, name: realName, email: patientObj?.email || "" },
        };
        sessionStorage.setItem("pendingSelectedContact", JSON.stringify(selectedContact));
        sessionStorage.setItem("autoSelectContactId", String(finalId));
        sessionStorage.setItem("autoSelectContactName", realName);
        const targetRoute = getNotificationRoute(n, "doctor");
        if (!targetRoute) return;
        navigate(targetRoute, { state: { selectedContact, autoOpen: true, fromNotif: true, fromUserId: finalId, patientId: finalId, patientName: realName, messageId: meta?.messageId || n?.messageId || null } });
        if (typeof onMessage === "function") onMessage(selectedContact);
        setNotifOpen(false);
        return;
      }

      if (isAccess) {
        const meta = getNotifMeta(n);
        const patientCandidate = normId(meta?.patientId || n?.patientId || meta?.targetUserId || n?.targetUserId || extractSenderIdFromNotif(n));
        const senderRaw = extractSenderIdFromNotif(n);
        const patientKey = resolvePatientKeyForMessaging(patientCandidate || senderRaw);
        const finalId = patientKey || patientCandidate || normId(senderRaw);

        if (finalId) {
          navigate(`/doctor/patients/${finalId}/DoctorOverviewsPage`, { state: { fromNotif: true, focus: "access" } });
        }
        setNotifOpen(false);
        return;
      }
      if (isCommentNotif(n)) {
        const targetRoute = getNotificationRoute(n, "doctor");
        if (targetRoute) navigate(targetRoute, { state: { fromNotif: true, notification: n } });
        setNotifOpen(false); return;
      }
      if (isAppointmentNotif(n)) {
        const targetRoute = getNotificationRoute(n, "doctor");
        if (targetRoute) navigate(targetRoute, { state: { fromNotif: true, notification: n } });
        setNotifOpen(false); return;
      }
      if (rawType.includes("prescription")) { navigate("/Perscription"); setNotifOpen(false); return; }
      if (rawType.includes("relative")) { navigate("/profile/relatives", { state: { fromNotif: true, focus: "requests" } }); setNotifOpen(false); return; }
      if (n?.link) navigate(n.link);
      setNotifOpen(false);
    },
    [markRead, navigate, doctorsList, resolvePatientKeyForMessaging, findPatientByAnyId, resolvePatientNameById, onMessage, t]
  );

  const fullName =
    `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim() ||
    currentUser?.fullName ||
    safeT(t, "submenu.defaults.doctorName", {}, "Doctor");

  const profileDropdownOverlay = (
    <div className="navbar__profile-dropdown" dir={isRTL ? "rtl" : "ltr"}>
      {!isProfileComplete && !completionLoading && (
        <div className="navbar__profile-dropdown__completion-banner" onClick={() => { navigate("/settingsdoctor"); setDropdownOpen(false); }}>
          <span className="navbar__profile-dropdown__completion-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </span>
          <span className="navbar__profile-dropdown__completion-text">
            {safeT(t, "submenu.profileProgress.completedPercent", { percent: profileCompletion }, `Your profile is only ${profileCompletion}% complete`)}
          </span>
        </div>
      )}
      <div className="navbar__profile-dropdown__menu">
        <div className="navbar__profile-dropdown__item" onClick={() => { navigate("/doctorprofile"); setDropdownOpen(false); }}>
          <span className="navbar__profile-dropdown__item-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="7.5" r="3.2" /><path d="M4 19.5c0-3.6 3.6-6.5 8-6.5s8 2.9 8 6.5" />
            </svg>
          </span>
          <span>{safeT(t, "submenu.menu.profile", {}, "My profile")}</span>
        </div>
        <div className="navbar__profile-dropdown__item" onClick={() => { navigate("/settingsdoctor"); setDropdownOpen(false); }}>
          <span className="navbar__profile-dropdown__item-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06A2 2 0 0 1 20.8 6.4l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </span>
          <span>{safeT(t, "submenu.menu.settings", {}, "Settings")}</span>
        </div>

        {/* ✅ Switch to Patient Mode */}
        <div
          className="navbar__profile-dropdown__item"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDropdownOpen(false);
            setTimeout(() => setSwitchPatientOpen(true), 0);
          }}
        >
          <span className="navbar__profile-dropdown__item-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 1l4 4-4 4" />
              <path d="M3 11V9a4 4 0 0 1 4-4h14" />
              <path d="M7 23l-4-4 4-4" />
              <path d="M21 13v2a4 4 0 0 1-4 4H3" />
            </svg>
          </span>
          <span>{safeT(t, "submenu.menu.switchToPatient", {}, "Switch to Patient Mode")}</span>
        </div>

        <div className="navbar__profile-dropdown__item" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDropdownOpen(false); setTimeout(() => setPrivacyOpen(true), 0); }}>
          <span className="navbar__profile-dropdown__item-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
            </svg>
          </span>
          <span>{safeT(t, "submenu.menu.policyPrivacy", {}, "Policy & Privacy")}</span>
        </div>
        <div className="navbar__profile-dropdown__item" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDropdownOpen(false); setTimeout(() => setFeedbackOpen(true), 0); }}>
          <span className="navbar__profile-dropdown__item-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </span>
          <span>{safeT(t, "submenu.menu.feedbackReclamations", {}, "Feedback & Reclamations")}</span>
        </div>
        <div className="navbar__profile-dropdown__divider" />
        <div className="navbar__profile-dropdown__item navbar__profile-dropdown__item--logout" onClick={() => { logout(); setDropdownOpen(false); }}>
          <span className="navbar__profile-dropdown__item-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </span>
          <span>{safeT(t, "submenu.menu.logout", {}, "Logout")}</span>
        </div>
      </div>
    </div>
  );

  const notifOverlay = (
    <div className="navbar__notif-dropdown" dir={isRTL ? "rtl" : "ltr"}>
      <div className="navbar__notif-header">
        <span className="navbar__notif-title">{safeT(t, "submenu.notifications.title", {}, "Notifications")}</span>
        <Button
          type="link"
          size="small"
          onClick={async (event) => {
            event.preventDefault();
            event.stopPropagation();
            try {
              await enablePushNotifications(currentUser?.id || currentUser?.userId || currentUser?.doctorId);
              message.success("Browser notifications enabled.");
            } catch (error) {
              message.info(error?.message || "Could not enable browser notifications.");
            }
          }}
          disabled={!currentUser?.id && !currentUser?.userId && !currentUser?.doctorId}
        >
          Enable browser alerts
        </Button>
        <Button type="link" size="small" onClick={(e) => { e.preventDefault(); e.stopPropagation(); markAllRead(); }} className="navbar__notif-seeall" disabled={rtUnreadCount === 0}>
          {safeT(t, "submenu.notifications.markAllAsRead", {}, "Mark all as read")}
        </Button>
      </div>
      <Divider style={{ margin: "8px 0" }} />
      {notifLoading ? (
        <div style={{ padding: 12, textAlign: "center" }}><Spin size="small" /></div>
      ) : !rtNotifications?.length ? (
        <div style={{ padding: 12 }}><Empty description={safeT(t, "submenu.notifications.empty", {}, "No notifications")} /></div>
      ) : (
        <div className="navbar__notif-list">
          {rtNotifications.map((n, idx) => {
            const id = n?.id || n?._id || `${idx}`;
            const isUnread = !n?.read;
            const text = formatNotifText(n);
            const date = n?.createdAt || n?.date;
            return (
              <div
                key={String(id)}
                className={`navbar__notif-item ${isUnread ? "navbar__notif-item--unread" : ""}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleNotifClick(n); }}
                role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") e.currentTarget.click(); }}
              >
                <div className="navbar__notif-dot" />
                <div className="navbar__notif-content">
                  <div className="navbar__notif-text">{text}</div>
                  {date && <div className="navbar__notif-date">{new Date(date).toLocaleString("en-GB")}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="navbar-wrapper" dir={isRTL ? "rtl" : "ltr"}>
      <nav className="navbar navbar--unified" dir={isRTL ? "rtl" : "ltr"}>
        <div className="navbar__left">
          {isMobile ? (
            <button className="navbar__mobile-menu-btn" onClick={onMobileMenuToggle} aria-label="Toggle menu">
              <MenuOutlined className="navbar__mobile-menu-icon" />
            </button>
          ) : (
            <div className="navbar__user-identity" onClick={() => navigate("/doctorprofile")} style={{ cursor: "pointer" }}>
              <div className="navbar__user-avatar-wrapper">
                {hasDoctorAvatar ? (
                  <Image src={avatarSrc} width={40} height={40} preview={false} className="navbar__user-avatar-img" onError={() => setAvatarLoadError(true)} style={{ borderRadius: "50%", objectFit: "cover" }} />
                ) : (
                  <div className="navbar__user-avatar-placeholder navbar__doctor-initials-avatar">{doctorInitials}</div>
                )}
              </div>
              <div className="navbar__user-info">
                <div className="navbar__user-name-row">
                  <span className="navbar__user-name">Dr. {fullName}</span>
                  {/* ✅ Badge cliquable - ouvre modal uniquement au clic */}
                  <DoctorStatusBanner
                    status={doctorStatus}
                    onRejectedClick={() => setRejectedModalOpen(true)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="navbar__right">
          {!isMobile && (
            <div className="navbar__search-container navbar__search-container--right">
              <input
                type="text"
                placeholder={safeT(
                  t,
                  searchTranslationKey("placeholder"),
                  {},
                  "Search doctors or patients…"
                )}
                className="navbar__search-input"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setSearchOpen(true);
                }}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
              />
              <button className="navbar__search-button">
                <img src={navbarSearchMutedIcon} alt="" aria-hidden="true" width="17" height="17" />
              </button>
              {searchOpen && searchSuggestions.length > 0 && (
                <div className="navbar__search-dropdown">
                  {searchSuggestions.map((p) => {
                    const hasAppt = hasAppointment(p);
                    const inContacts = isInMessageContacts(p);
                    const isDoctorResult = p._searchRole === "doctor";
                    const resultName = p.fullName || `${p.firstName || ""} ${p.lastName || ""}`.trim() ||
                      p?.user?.fullName || `${p?.user?.firstName || ""} ${p?.user?.lastName || ""}`.trim() ||
                      safeT(t, searchTranslationKey("unknown"), {}, "Unknown");
                    return (
                      <div
                        key={p.id || p._id || p.userId || Math.random()}
                        className="navbar__search-item"
                        onMouseDown={() => handleSearchSelect(p)}
                        title={`ID: ${p.id || p._id || "N/A"}`}
                        style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}
                      >
                        <div style={{ flex: 1 }}>
                          <strong>
                            {isDoctorResult && `${safeT(t, searchTranslationKey("doctorPrefix"), {}, "Dr.")} `}
                            {resultName}
                          </strong>
                          <div className="navbar__search-sub">
                            {isDoctorResult
                              ? safeT(t, searchTranslationKey("doctorLabel"), {}, "Doctor")
                              : safeT(t, searchTranslationKey("patientLabel"), {}, "Patient")}
                          </div>
                          {p.email && <div className="navbar__search-sub">{p.email}</div>}
                          {p.phone && <div className="navbar__search-sub">{p.phone}</div>}
                        </div>
                        <div style={{ display: "flex", gap: "6px" }}>
                          {inContacts && <Badge count="💬" style={{ backgroundColor: "#1890ff", color: "white", fontSize: "11px", fontWeight: "bold", padding: "0 4px", minWidth: "24px", height: "24px", lineHeight: "24px", textAlign: "center", borderRadius: "4px" }} />}
                          {hasAppt && <Badge count="📅" style={{ backgroundColor: "#52c41a", color: "white", fontSize: "11px", fontWeight: "bold", padding: "0 4px", minWidth: "24px", height: "24px", lineHeight: "24px", textAlign: "center", borderRadius: "4px" }} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {searchOpen && searchValue && searchSuggestions.length === 0 && (
                <div className="navbar__search-dropdown">
                  <div style={{ padding: "12px 20px", fontSize: "13px", color: "#666", textAlign: "center" }}>
                    <div style={{ marginBottom: "8px" }}>🔍 {safeT(t, searchTranslationKey("noUserFound"), { query: searchValue }, `No doctor or patient found matching "${searchValue}"`)}</div>
                    <div style={{ fontSize: "11px", color: "#999" }}>{safeT(t, searchTranslationKey("tryDifferentName"), {}, "Try searching with a different name, email, or phone")}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          <NavbarDateWeather />
          <LanguageSelector />
          <NavbarRadio />

          <div className="navbar__icon-button navbar__icon-button--theme" onClick={toggleTheme} role="button" tabIndex={0}
            aria-label={theme === "dark" ? safeT(t, "submenu.theme.enableLight", {}, "Switch to light mode") : safeT(t, "submenu.theme.enableDark", {}, "Switch to dark mode")}>
            {theme === "dark" ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21 12.79A9 9 0 0 1 12.79 3 7 7 0 1 0 21 12.79Z" /></svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <circle cx="12" cy="12" r="5" />
                <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                  <line x1="12" y1="2" x2="12" y2="5" /><line x1="12" y1="19" x2="12" y2="22" />
                  <line x1="4.22" y1="4.22" x2="6.34" y2="6.34" /><line x1="17.66" y1="17.66" x2="19.78" y2="19.78" />
                  <line x1="2" y1="12" x2="5" y2="12" /><line x1="19" y1="12" x2="22" y2="12" />
                  <line x1="4.22" y1="19.78" x2="6.34" y2="17.66" /><line x1="17.66" y1="6.34" x2="19.78" y2="4.22" />
                </g>
              </svg>
            )}
          </div>

          <Dropdown overlay={notifOverlay} placement="bottomRight" trigger={["click"]} open={notifOpen}
            onOpenChange={(v) => { setNotifOpen(v); if (v) setDropdownOpen(false); }}
            overlayClassName="navbar__notif-overlay" overlayStyle={{ padding: 0, position: "fixed", zIndex: 9999 }}
            getPopupContainer={() => document.body} destroyPopupOnHide={false}>
            <div className="navbar__icon-button navbar__icon-button--notification" role="button" aria-label={safeT(t, "submenu.notifications.aria", {}, "Notifications")}>
              <Badge count={rtUnreadCount} overflowCount={99} size="small">
                <BellOutlined style={{ fontSize: 20, color: "#939597" }} />
              </Badge>
            </div>
          </Dropdown>

          <Dropdown overlay={profileDropdownOverlay} placement="bottomRight" trigger={["click"]} open={dropdownOpen}
            onOpenChange={(v) => { setDropdownOpen(v); if (v) setNotifOpen(false); }}
            overlayClassName="navbar__profile-dropdown-overlay" overlayStyle={{ padding: 0, position: "fixed", zIndex: 9999 }}
            getPopupContainer={() => document.body}>
            <div className="navbar__profile-trigger" role="button" tabIndex={0}>
              <div className="navbar__profile-progress-avatar" style={{ "--profile-progress-angle": `${safeDoctorCompletionPercent * 3.6}deg` }}>
                <div className="navbar__profile-progress-avatar-inner">
                  {hasDoctorAvatar ? (
                    <img src={avatarSrc} alt={fullName} className="navbar__profile-progress-avatar-img" onError={() => setAvatarLoadError(true)} />
                  ) : (
                    <div className="navbar__profile-progress-avatar-placeholder navbar__doctor-initials-avatar">{doctorInitials}</div>
                  )}
                </div>
              </div>
              <span className={`navbar__profile-trigger-chevron ${dropdownOpen ? "navbar__profile-trigger-chevron--open" : ""}`}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </div>
          </Dropdown>
        </div>
      </nav>

      <FeedbackPopup
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        user={currentUser}
        onSubmitFeedback={async (payload) => {
        }}
      />

      {/* ✅ Modal s'ouvre UNIQUEMENT au login (via sessionStorage) OU au clic sur le badge */}
      <RejectedDoctorModal
        open={rejectedModalOpen}
        onClose={() => setRejectedModalOpen(false)}
        rejectionReason={doctorRejectionReason}
        onGoToProfile={() => navigate("/settingsdoctor")}
      />

      {/* ✅ Modal Switch to Patient Mode */}
      <SwitchToPatientModal
        open={switchPatientOpen}
        onClose={() => setSwitchPatientOpen(false)}
        doctor={currentUser}
      />
    </div>
  );
}